import React, { useEffect, useState, useRef, useMemo } from 'react';
import type { MenuProps } from 'antd';
import routes from 'routes';
import { useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import {
  AppSideMenu,
  IconNews,
  IconNewsBlinking,
  SIDE_MENU_ICON_STYLE,
} from '@monorepo/shared-lib';
import { ReactComponent as IconHome } from 'assets/images/icons/iconHome.svg';
import { ReactComponent as IconReturns } from 'assets/images/icons/iconReturns.svg';
import { ReactComponent as IconEntities } from 'assets/images/icons/iconEntities.svg';
import { ReactComponent as IconVIES } from 'assets/images/icons/iconVIES.svg';
import { ReactComponent as IconUsers } from 'assets/images/icons/iconUsers.svg';
import { ReactComponent as IconSupport } from 'assets/images/icons/iconSupport.svg';
import { LicenseType, MenuSize } from 'store/app/enums';
import { User, UserLicence } from 'store/auth/models';
import { setNewReleaseSeenRequest } from 'store/user/actions';
import { useTranslation } from 'react-i18next';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
interface SideMenuProps {
  menuSize: MenuSize;
  redirectPage: (route: string) => void;
  currentUser?: User | undefined;
  userLicence?: UserLicence;
  isNewReleaseSeen: boolean;
}

export const SideMenu: React.FC<SideMenuProps> = ({
  redirectPage,
  menuSize,
  currentUser,
  userLicence,
  isNewReleaseSeen = true,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const [current, setCurrent] = useState(location.pathname);
  const isMountedRef = useRef(true);

  const WHATS_NEW_URL =
    'https://help.generate.tax/portal/en/community/generate-tax';

  const onClick: MenuProps['onClick'] = (e) => {
    // Check if it's the "What's new" link and if release hasn't been seen
    if (
      e.key === WHATS_NEW_URL &&
      !isNewReleaseSeen &&
      currentUser?.accountUuid
    ) {
      // Dispatch action to mark release as seen before opening the link
      dispatch(setNewReleaseSeenRequest(currentUser.accountUuid));
    }
    redirectPage(e.key);
  };
  const isDisabled =
    userLicence?.licenseType === LicenseType.BLOCKED ||
    (userLicence?.licenseType === LicenseType.TRIAL &&
      userLicence?.licenseDaysLeft === 0);
  const items: MenuProps['items'] = useMemo(
    () => [
      {
        label: t('navigation.home'),
        key: routes.main,
        icon: <IconHome style={{ width: 20, height: 20 }} />,
        disabled: isDisabled,
      },
      {
        label: t('navigation.returns'),
        key: routes.submissions,
        icon: <IconReturns style={{ width: 20, height: 20 }} />,
        disabled: isDisabled,
      },
      // Planner: hidden from menu but still accessible via URL (/planner)
      // {
      //   label: t('navigation.planner'),
      //   key: routes.planner,
      //   icon: <IconCalendar style={{ width: 20, height: 20 }} />,
      //   disabled: isDisabled,
      // },
      {
        label: t('organisation.entities'),
        key: '/entities',
        icon: <IconEntities style={{ width: 20, height: 20 }} />,
        disabled: isDisabled,
      },
      // {
      //   label: t('navigation.reporting'),
      //   key: routes.reporting,
      //   icon: <IconReporting />,
      // },
      ...(pwaMobileNav
        ? []
        : [
            {
              label: t('navigation.vat-checker'),
              key: routes.vies.qualifiedEntities,
              icon: <IconVIES style={{ width: 20, height: 20 }} />,
              disabled: isDisabled,
            },
          ]),
      {
        label: t('main.users'),
        key: routes.users,
        icon: <IconUsers style={{ width: 20, height: 20 }} />,
        disabled: isDisabled,
      },
      { type: 'divider' },
      {
        label: t('navigation.support'),
        key: 'https://help.generate.tax/portal/en/home',
        icon: <IconSupport style={{ width: 20, height: 20 }} />,
        disabled: isDisabled,
      },
      {
        label: t('navigation.whats-new'),
        key: WHATS_NEW_URL,
        icon: isNewReleaseSeen ? (
          <IconNews style={SIDE_MENU_ICON_STYLE} />
        ) : (
          <IconNewsBlinking style={SIDE_MENU_ICON_STYLE} />
        ),
        disabled: isDisabled,
      },
    ],
    [t, isDisabled, isNewReleaseSeen, pwaMobileNav]
  );
  useEffect(() => {
    isMountedRef.current = true;

    if (isMountedRef.current) {
      setCurrent(location.pathname);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [location]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /* const getMenuItems = () => {
    if (
      (userLicence?.licenseType === LicenseType.TRIAL &&
        userLicence?.licenseDaysLeft === 0) ||
      userLicence?.licenseType === LicenseType.BLOCKED
    ) {
      return trialExpireItems;
    } else {
      if (
        currentUser?.accountType &&
        AccountType[currentUser?.accountType] == AccountType.OWNER
      ) {
        if (userLicence?.licenseType === LicenseType.TRIAL) {
          return itemsWithPricing;
        } else {
          return items;
        }
      } else {
        if (userLicence?.licenseType === LicenseType.TRIAL) {
          return clientTrialExpireItems;
        } else {
          return itemsWithoutBilling;
        }
      }
    }
  }; */

  return (
    <AppSideMenu
      size={menuSize === MenuSize.WIDE ? 'wide' : 'narrow'}
      onClick={onClick}
      selectedKeys={[current]}
      items={items}
    />
  );
};
